import { createSlice } from "@reduxjs/toolkit";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { FetchingStatus } from "@store/store-helper";
import {
  fetchCurrentUser,
  fetchCurrentUserRoles,
} from "@store/user/user-thunks";

/** State of the current User */
type UserState = {
  /** Current user */
  currentUser: FaroCoreApiTypes.LoggedInUser | null;

  /** The roles of the logged in user */
  currentUserRoles: FaroCoreApiTypes.AdmUserRole[];

  /** Async operation statuses for user-slice */
  fetchingUser: {
    /** Flag indicating if the user data is being fetched */
    currentUser: FetchingStatus;

    /** Flag indicating if the user roles are being fetched */
    currentUserRoles: FetchingStatus;
  };
};

const initialUserState: UserState = {
  currentUser: null,

  currentUserRoles: [],

  fetchingUser: {
    currentUser: "uninitialized",
    currentUserRoles: "uninitialized",
  },
};

/** Slice to access state of current-user data */
const userSlice = createSlice({
  name: "user",
  initialState: initialUserState,
  reducers: {
    resetUserState: () => initialUserState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCurrentUser.pending, (state) => {
        state.fetchingUser.currentUser = "pending";
      })
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        state.currentUser = action.payload;
        state.fetchingUser.currentUser = "succeeded";
      })
      .addCase(fetchCurrentUser.rejected, (state) => {
        state.fetchingUser.currentUser = "rejected";
      })
      .addCase(fetchCurrentUserRoles.pending, (state) => {
        state.fetchingUser.currentUserRoles = "pending";
      })
      .addCase(fetchCurrentUserRoles.fulfilled, (state, action) => {
        state.currentUserRoles = action.payload;
        state.fetchingUser.currentUserRoles = "succeeded";
      })
      .addCase(fetchCurrentUserRoles.rejected, (state) => {
        state.fetchingUser.currentUserRoles = "rejected";
      });
  },

  selectors: {
    /** Selector to get the entire user state */
    userStateSelector: (state) => state,
  },
});

export const { resetUserState } = userSlice.actions;

export const { userStateSelector } = userSlice.selectors;

export const userReducer = userSlice.reducer;
